import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import ImageUpload from "../../Components/ImageUpload";

const EMPTY_FORM = {
  image: "",
  headline: "",
  subheadline: "",
  buttonText: "",
  category: "",
  sortOrder: "0",
  active: true,
};

// The homepage hero carousel used to be a hardcoded array in HomeHero.jsx
// - no admin page, so changing a slide (or reordering the seasonal
// rotation) meant editing code and redeploying. This is the real
// management UI for it.
const Sliders = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const token = localStorage.getItem("authToken");

  const fetchSlides = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/slide?all=true`);
      setSlides(response.data.slides);
    } catch (err) {
      console.error("Error fetching slides:", err);
      setError("Couldn't load slides.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlides();
  }, []);

  const startCreate = () => {
    setError(null);
    setEditingId(null);
    setFormData({ ...EMPTY_FORM, sortOrder: String(slides.length) });
  };

  const startEdit = (slide) => {
    setError(null);
    setEditingId(slide._id);
    setFormData({
      image: slide.image,
      headline: slide.headline,
      subheadline: slide.subheadline,
      buttonText: slide.buttonText,
      category: slide.category || "",
      sortOrder: String(slide.sortOrder),
      active: slide.active,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.image || !formData.headline.trim() || !formData.subheadline.trim() || !formData.buttonText.trim()) {
      setError("Image, headline, subheadline, and button text are all required.");
      return;
    }

    const payload = { ...formData, sortOrder: Number(formData.sortOrder) || 0 };

    setSubmitting(true);
    try {
      if (editingId) {
        await axios.put(`${API_BASE_URL}/slide/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API_BASE_URL}/slide`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      startCreate();
      await fetchSlides();
    } catch (err) {
      console.error("Error saving slide:", err);
      setError(err.response?.data?.message || "Failed to save.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (slide) => {
    try {
      await axios.put(
        `${API_BASE_URL}/slide/${slide._id}`,
        { active: !slide.active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchSlides();
    } catch (err) {
      console.error("Error updating slide:", err);
      alert(err.response?.data?.message || "Failed to update slide.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this slide? This can't be undone.")) return;
    try {
      await axios.delete(`${API_BASE_URL}/slide/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (editingId === id) startCreate();
      await fetchSlides();
    } catch (err) {
      console.error("Error deleting slide:", err);
      alert(err.response?.data?.message || "Failed to delete.");
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-sub">Loading slides…</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4 text-ink">Homepage Slider</h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {slides.length === 0 ? (
            <p className="text-sub">No slides yet - add the first one.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {slides.map((slide) => (
                <div key={slide._id} className="bg-card border border-line rounded-lg p-4">
                  <img
                    src={slide.image}
                    alt={slide.headline}
                    className="w-full h-32 object-cover rounded mb-2"
                  />
                  <h3 className="text-lg font-semibold text-ink">{slide.headline}</h3>
                  <p className="text-sm text-sub">{slide.subheadline}</p>
                  <p className="text-xs text-muted mt-1">
                    Button: "{slide.buttonText}" → {slide.category ? `/menu?category=${slide.category}` : "/menu"}
                  </p>
                  <span
                    className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${
                      slide.active ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
                    }`}
                  >
                    {slide.active ? "Active" : "Hidden"}
                  </span>
                  <div className="flex justify-between mt-4">
                    <button
                      onClick={() => startEdit(slide)}
                      className="bg-primary text-white py-2 px-4 rounded hover:opacity-90 transition"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => toggleActive(slide)}
                      className="text-xs font-semibold text-ink border border-line px-3 rounded hover:bg-paper transition"
                    >
                      {slide.active ? "Hide" : "Show"}
                    </button>
                    <button
                      onClick={() => handleDelete(slide._id)}
                      className="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-card border border-line rounded-lg p-6 space-y-4 h-fit"
        >
          <h3 className="text-lg font-semibold text-ink">
            {editingId ? "Edit slide" : "Add a slide"}
          </h3>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </p>
          )}

          <ImageUpload
            label="Image"
            value={formData.image}
            onChange={(image) => setFormData({ ...formData, image })}
          />
          <input
            placeholder="Headline"
            value={formData.headline}
            onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
            className="w-full p-2 border border-line rounded"
          />
          <input
            placeholder="Subheadline"
            value={formData.subheadline}
            onChange={(e) => setFormData({ ...formData, subheadline: e.target.value })}
            className="w-full p-2 border border-line rounded"
          />
          <input
            placeholder="Button text (e.g. Lunch Menu)"
            value={formData.buttonText}
            onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })}
            className="w-full p-2 border border-line rounded"
          />
          <div>
            <label className="block font-semibold mb-1 text-ink">
              Category slug (optional)
            </label>
            <input
              placeholder="e.g. lunch - leave blank for a generic slide"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full p-2 border border-line rounded"
            />
            <p className="text-xs text-sub mt-1">
              Matches a category slug under Categories. The button links to that category's
              menu, or to the general menu if left blank.
            </p>
          </div>
          <div>
            <label className="block font-semibold mb-1 text-ink">Order</label>
            <input
              type="number"
              value={formData.sortOrder}
              onChange={(e) => setFormData({ ...formData, sortOrder: e.target.value })}
              className="w-full p-2 border border-line rounded"
            />
          </div>
          <label className="flex items-center text-ink">
            <input
              type="checkbox"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              className="mr-2"
            />
            Active (shown on homepage)
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-primary text-white p-3 rounded-lg font-bold hover:opacity-90 transition disabled:opacity-60"
          >
            {submitting ? "Saving…" : editingId ? "Save Changes" : "Add Slide"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={startCreate}
              className="w-full border border-line text-ink py-2 rounded hover:bg-paper transition"
            >
              Cancel Edit
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default Sliders;
